import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { awsEnvironmentsAPI } from '../services/realApi';

export interface OnboardingStep {
  id: string;
  label: string;
  description: string;
  route: string;
  section: string;
}

/**
 * Onboarding steps for CloudOps
 * Each step has:
 * - id: unique identifier
 * - label: display name
 * - description: what this step accomplishes
 * - route: where to go to complete this step
 * - section: grouping for visual organization
 */
export const CLOUDOPS_ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'aws_account',
    label: 'Connect AWS Account',
    description: 'Link your AWS account to enable cloud management and monitoring',
    route: '/hub/aws-environments',
    section: 'setup'
  },
  {
    id: 'review_costs',
    label: 'Review FinOps Dashboard',
    description: 'Explore your cloud spending and identify optimization opportunities',
    route: '/cloudops/finops',
    section: 'monitor'
  },
  {
    id: 'check_anomalies',
    label: 'Check Cost Anomalies',
    description: 'Review any unusual spending patterns in your accounts',
    route: '/cloudops/anomalies',
    section: 'monitor'
  },
  {
    id: 'configure_guardrails',
    label: 'Configure Guardrails',
    description: 'Set up security and compliance guardrails for your infrastructure',
    route: '/cloudops/guardrails',
    section: 'governance'
  }
];

// Icon mapping for steps
export const CLOUDOPS_STEP_ICONS: Record<string, string> = {
  aws_account: 'FiCloud',
  review_costs: 'FiDollarSign',
  check_anomalies: 'FiAlertTriangle',
  configure_guardrails: 'FiShield'
};

// Section labels
export const CLOUDOPS_SECTIONS: Record<string, string> = {
  setup: 'Setup',
  monitor: 'Monitor',
  governance: 'Governance'
};

interface CloudOpsOnboardingStatus {
  hasAwsAccount: boolean;
  hasReviewedCosts: boolean;
  hasCheckedAnomalies: boolean;
  hasConfiguredGuardrails: boolean;
  loading: boolean;
  error: string | null;
}

type StepStatusMap = Record<string, boolean>;

interface UseCloudOpsOnboardingStatusReturn extends CloudOpsOnboardingStatus {
  stepStatusMap: StepStatusMap;
  completedSteps: number;
  totalSteps: number;
  completionPercentage: number;
  nextStep: OnboardingStep | undefined;
  isComplete: boolean;
  refresh: () => Promise<void>;
}

/**
 * Hook to track CloudOps onboarding progress
 * Checks various APIs to determine which steps are complete
 */
export const useCloudOpsOnboardingStatus = (): UseCloudOpsOnboardingStatusReturn => {
  const { user } = useAuth();
  const [status, setStatus] = useState<CloudOpsOnboardingStatus>({
    hasAwsAccount: false,
    hasReviewedCosts: false,
    hasCheckedAnomalies: false,
    hasConfiguredGuardrails: false,
    loading: true,
    error: null
  });

  const checkStatus = useCallback(async () => {
    if (!user) {
      setStatus(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      const clientId = user?.client_id;

      // Check for AWS accounts
      let hasAwsAccount = false;
      if (clientId) {
        try {
          const response = await awsEnvironmentsAPI.getAccounts(clientId as any);
          const accounts = response?.data || response || [];
          const accountsArray = Array.isArray(accounts) ? accounts : [];
          hasAwsAccount = accountsArray.length > 0;
        } catch {
          hasAwsAccount = false;
        }
      }

      // For now, we'll use localStorage to track "viewed" states for pages
      // In a production app, this would be tracked server-side
      const hasReviewedCosts = localStorage.getItem('cloudops_reviewed_costs') === 'true';
      const hasCheckedAnomalies = localStorage.getItem('cloudops_checked_anomalies') === 'true';
      const hasConfiguredGuardrails = localStorage.getItem('cloudops_configured_guardrails') === 'true';

      setStatus({
        hasAwsAccount,
        hasReviewedCosts,
        hasCheckedAnomalies,
        hasConfiguredGuardrails,
        loading: false,
        error: null
      });
    } catch (err) {
      console.error('Error checking CloudOps onboarding status:', err);
      setStatus(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : String(err)
      }));
    }
  }, [user]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Calculate completion
  const completedSteps = [
    status.hasAwsAccount,
    status.hasReviewedCosts,
    status.hasCheckedAnomalies,
    status.hasConfiguredGuardrails
  ].filter(Boolean).length;

  const totalSteps = CLOUDOPS_ONBOARDING_STEPS.length;
  const completionPercentage = Math.round((completedSteps / totalSteps) * 100);

  // Map step IDs to their completion status
  const stepStatusMap: StepStatusMap = {
    aws_account: status.hasAwsAccount,
    review_costs: status.hasReviewedCosts,
    check_anomalies: status.hasCheckedAnomalies,
    configure_guardrails: status.hasConfiguredGuardrails
  };

  // Find next incomplete step
  const nextStep = CLOUDOPS_ONBOARDING_STEPS.find(step => !stepStatusMap[step.id]);
  const isComplete = completedSteps === totalSteps;

  return {
    ...status,
    stepStatusMap,
    completedSteps,
    totalSteps,
    completionPercentage,
    nextStep,
    isComplete,
    refresh: checkStatus
  };
};

// Helper function to mark a step as complete (for page views)
export const markCloudOpsStepComplete = (stepId: string): void => {
  const stepKeys: Record<string, string> = {
    review_costs: 'cloudops_reviewed_costs',
    check_anomalies: 'cloudops_checked_anomalies',
    configure_guardrails: 'cloudops_configured_guardrails'
  };

  const key = stepKeys[stepId];
  if (key) {
    localStorage.setItem(key, 'true');
  }
};

export default useCloudOpsOnboardingStatus;
